import { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const KEY = "nc_cart";

const readCart = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || "[]"); } catch { return []; }
};
const writeCart = (items) => {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* ignore */ }
};

export const CartProvider = ({ children }) => {
  const [items, setItemsState] = useState(readCart);

  const setItems = (updater) => {
    setItemsState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      writeCart(next);
      return next;
    });
  };

  // safety sync across tabs/components
  useEffect(() => { writeCart(items); }, [items]);

  const keyOf = (product_id, variant_id) => `${product_id}::${variant_id || ""}`;

  const addItem = (product, qty = 1, note = "", variant = null) => {
    setItems((prev) => {
      const key = keyOf(product.id, variant?.id);
      const existing = prev.find((it) => keyOf(it.product_id, it.variant_id) === key);
      if (existing) {
        return prev.map((it) =>
          keyOf(it.product_id, it.variant_id) === key
            ? { ...it, quantity: it.quantity + qty, note: note ? note : (it.note || "") }
            : it
        );
      }
      // Prefer variant image / stock when a variant is picked
      const variantImage = variant?.images?.[0];
      const variantStock = variant ? variant.stock : product.stock;
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          quantity: qty,
          image: variantImage || product.images?.[0] || "",
          slug: product.slug,
          stock: variantStock,
          note: note || "",
          variant_id: variant?.id || null,
          variant_color_name: variant?.color_name || null,
          variant_color_hex: variant?.color_hex || null,
        },
      ];
    });
  };

  const updateNote = (product_id, note, variant_id = null) => {
    const key = keyOf(product_id, variant_id);
    setItems((prev) => prev.map((it) => (keyOf(it.product_id, it.variant_id) === key ? { ...it, note } : it)));
  };

  const updateQty = (product_id, qty, variant_id = null) => {
    if (qty <= 0) return removeItem(product_id, variant_id);
    const key = keyOf(product_id, variant_id);
    setItems((prev) => prev.map((it) => (keyOf(it.product_id, it.variant_id) === key ? { ...it, quantity: qty } : it)));
  };

  const removeItem = (product_id, variant_id = null) => {
    const key = keyOf(product_id, variant_id);
    setItems((prev) => prev.filter((it) => keyOf(it.product_id, it.variant_id) !== key));
  };
  const clear = () => setItems([]);

  const total = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const count = items.reduce((sum, it) => sum + it.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, updateQty, updateNote, removeItem, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
